// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';

/**
 * Schema for validating the API response when deleting a note.
 * Transforms the response to provide a more intuitive structure with
 * the success flag separated from metadata.
 */
const deleteNoteResponseSchema = z
  .object({
    data: z.object({ success: z.boolean() }),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    success: data.success,
  }));

/**
 * Response type for note deletion operations.
 * @property {boolean} success - Whether the deletion was successful
 * @property {string} requestId - Unique identifier for the request
 */
type DeleteNoteResponse = z.infer<typeof deleteNoteResponseSchema>;

/**
 * Delete a note
 * @param noteKey The key of the note to delete
 * @returns A promise that resolves to the delete note response
 */
export async function deleteNote(noteKey: string): Promise<DeleteNoteResponse> {
  const url = `/v1/note/${noteKey}`;
  // Include the noteKey in the request body as required by the backend
  const response = await api.delete(url, {
    data: { noteKey },
  });

  return parseResponse(deleteNoteResponseSchema, response.data, {
    schemaName: 'DeleteNote',
    endpoint: url,
  });
}
